import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { trackEvent } from '../utils/analytics';

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dby1smfuo';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'unsigned_upload';
const MAX_PHOTOS = 5;

function MaintenanceRequestForm() {
  const { user } = useContext(AuthContext);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { setError(`Maximum ${MAX_PHOTOS} photos allowed.`); return; }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    setError('');
    try {
      const urls = [];
      for (const file of toUpload) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', UPLOAD_PRESET);
        formData.append('folder', 'maintenance');
        const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method: 'POST', body: formData });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        urls.push(data.secure_url);
      }
      setPhotos(prev => [...prev, ...urls]);
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || user.userType !== 'tenant') return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/maintenance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ title, description, photo_urls: photos })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to submit request');
      setSuccess('Request submitted');
      setTitle('');
      setDescription('');
      setPhotos([]);

      trackEvent({
        event_name: 'maintenance_request_created',
        event_category: 'maintenance',
        event_action: 'submit'
      });
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dossier-section maintenance-card">
      <h3>Maintenance Request</h3>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="status-text">{success}</p>}
      <form onSubmit={handleSubmit} className="maintenance-form">
        <input
          type="text"
          placeholder="Issue title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <textarea
          placeholder="Describe the issue"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          required
        />
        <div className="form-group">
          <label>Photos (optional, up to {MAX_PHOTOS})</label>
          {photos.length < MAX_PHOTOS && (
            <label className="primary-button" style={{ display: 'inline-block', cursor: 'pointer', marginBottom: '0.5rem' }}>
              {uploading ? 'Uploading...' : 'Upload images'}
              <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} disabled={uploading} className="file-input-hidden" />
            </label>
          )}
          {photos.length > 0 && (
            <div className="image-preview-grid">
              {photos.map((url, i) => (
                <div key={i} className="image-preview-container">
                  <img src={url} alt={`Upload ${i + 1}`} className="image-preview" />
                  <button type="button" onClick={() => removePhoto(i)} className="remove-image-button" title="Remove image">&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="primary-button" type="submit" disabled={loading || uploading}>
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
}

export default MaintenanceRequestForm;
